import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact, { BugsnagErrorBoundary as BugsnagErrorBoundaryType } from '@bugsnag/plugin-react';
import React, { useEffect, useState } from 'react';

import { getEnv } from '@/app/conf/env';
import { UnhandledErrorDisplay } from '@/shared/components/errors/unhandled-error-display';

/**
 * Wraps children with a Bugsnag error boundary.
 * It initializes Bugsnag with the appropriate configuration and provides
 * error handling functionality using the Bugsnag React plugin.
 */
export function BugsnagErrorBoundary({ children }: { children: React.ReactNode }) {
  const [bugsnagErrorBoundary, setBugsnagErrorBoundary] = useState<BugsnagErrorBoundaryType | null>(null);
  const env = getEnv();

  useEffect(() => {
    if (!Bugsnag.isStarted?.()) {
      Bugsnag.start({
        apiKey: env.BUGSNAG_API_KEY,
        plugins: [new BugsnagPluginReact()],
        releaseStage: env.APP_ENV,
        appVersion: env.APP_VERSION,
        enabledReleaseStages: env.BUGSNAG_ENABLED_RELEASE_STAGES
      });
    }

    const ErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(React) || null;

    if (!ErrorBoundary) {
      throw new Error('Unable to initialize Bugsnag error boundary');
    }

    setBugsnagErrorBoundary(() => ErrorBoundary);
  }, [env]);

  if (!bugsnagErrorBoundary) {
    return <>{children}</>;
  }

  const BugsnagErrorBoundaryComponent = bugsnagErrorBoundary;

  return (
    <BugsnagErrorBoundaryComponent FallbackComponent={BugsnagErrorFallback}>
      {children}
    </BugsnagErrorBoundaryComponent>
  );
}

function BugsnagErrorFallback({ error, clearError }: { error: Error; clearError: () => void }) {
  return (
    <UnhandledErrorDisplay error={error} resetErrorBoundary={clearError} />
  );
}
